import {
  getRoles,
  updateRole,
  delRole,
  roleAddUsers,
  roleDelUsers,
  roleAddPermissions,
  roleDelPermissions,
} from '@/api/role';
import { allPermission, generatePermissionComment, sortPermissionsByGroupName } from '@/utils/permission';
import { companyAllActiveUser } from '@/utils/user';
import { AccountModule, getPermissionNames } from '@/store/modules/account';
import moment from 'moment';
import { generateUUID } from '@/utils/other';

type IRoleInfo = IRole.IResponse.IRoleInfo;
type ICreateRoleOrUpdateParams = IRole.IRequest.ICreateRoleOrUpdateParams;

interface ITableRoleInfo extends IRoleInfo {
  hasEditPermission: boolean;
}

interface IRoleManage {
  data: IRoleInfo[];
  editData: {
    roleInfo: IRoleInfo | null;
    isCreate: boolean;
  };
  requestGetRoles(): Promise<string>;
  requestUpdateRole(role: ICreateRoleOrUpdateParams): Promise<string>;
  requestDelRole(roleId: string): Promise<string>;
  requestAddUsers(userIds: string[]): Promise<string>;
  requestRemoveUsers(userIds: string[]): Promise<string>;
  requestAddPermissions(permissionIds: string[]): Promise<string>;
  requestDelPermissions(permissionIds: string[]): Promise<string>;
  updateRole(role: IRoleInfo): void;
  createRole(): void;
  editRole(roleInfo: IRoleInfo): void;
  addRole(role: IRoleInfo): void;
  updateRoleList(role: ICreateRoleOrUpdateParams): void;
  addRoleUsers(userIds: string[]): void;
  removeRoleUsers(userIds: string[]): void;
  addRolePermissions(permissionIds: string[]): void;
  delRolePermissions(permissionIds: string[]): void;
  delRole(roleId: string): void;
}

const mapRoles = (roles: IRoleInfo[]): IRoleInfo[] => {
  return roles.map((role) => {
    role.permissions = generatePermissionComment(role.permissions);
    return role;
  });
};


class RoleManage implements IRoleManage {
  private $data: IRoleInfo[] = [];
  private $editData: {
    roleInfo: IRoleInfo | null;
    isCreate: boolean;
  } = {
    roleInfo: null,
    isCreate: false,
  };
  get data() {
    return this.$data;
  }
  get editData() {
    return this.$editData;
  }
  get tableData() {
    return enrichRoleInfo(this.$data);
  }
  public requestGetRoles(): Promise<string> {
    return new Promise((resolve, reject) => {
      getRoles({
        returnUser: true,
        returnPermission: true,
      }).then((res) => {
        this.$data = mapRoles(res.data);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestUpdateRole(role: ICreateRoleOrUpdateParams): Promise<string> {
    return new Promise((resolve, reject) => {
      updateRole({
        roleId: this.$editData.roleInfo!.id,
        data: role,
      }).then((res) => {
        this.updateRoleList(role);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestDelRole(roleId: string): Promise<string>  {
    return new Promise((resolve, reject) => {
      delRole(roleId).then((res) => {
        this.delRole(roleId);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestAddUsers(userIds: string[]): Promise<string> {
    return new Promise((resolve, reject) => {
      roleAddUsers({
        roleId: this.$editData.roleInfo!.id,
        userIds,
      }).then((res) => {
        this.addRoleUsers(userIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestRemoveUsers(userIds: string[]): Promise<string> {
    return new Promise((resolve, reject) => {
      roleDelUsers({
        roleId: this.$editData.roleInfo!.id,
        userIds,
      }).then((res) => {
        this.removeRoleUsers(userIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestAddPermissions(permissionIds: string[]): Promise<string> {
    return new Promise((resolve, reject) => {
      roleAddPermissions({
        roleId: this.$editData.roleInfo!.id,
        permissionIds,
      }).then((res) => {
        this.addRolePermissions(permissionIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestDelPermissions(permissionIds: string[]): Promise<string> {
    return new Promise((resolve, reject) => {
      roleDelPermissions({
        roleId: this.$editData.roleInfo!.id,
        permissionIds,
      }).then((res) => {
        this.delRolePermissions(permissionIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public updateRole(role: IRoleInfo) {
    for (const roleItem of this.$data) {
      if (roleItem.id === role.id) {
        Object.assign(roleItem, role);
        break;
      }
    }
  }
  public createRole() {
    this.$editData = {
      roleInfo: {
        id: generateUUID(),
        name: '',
        comment: '',
        createdName: '',
        createdOn: '',
        createdBy: '',
        permissions: [],
        user: [],
        active: true,
      },
      isCreate: true,
    };
  }
  public editRole(roleInfo: IRoleInfo) {
    this.$editData = {
      roleInfo: {...roleInfo},
      isCreate: false,
    };
  }
  public addRole(role: IRoleInfo) {
    this.$data.unshift(role);
  }
  public updateRoleList(role: ICreateRoleOrUpdateParams) {
    const newRole: IRoleInfo = {
      id: this.$editData.roleInfo!.id,
      name: role.name,
      comment: role.comment,
      active: role.active,
      createdName: this.$editData.roleInfo?.createdName || AccountModule.name,
      createdOn: this.$editData.roleInfo?.createdOn || moment().toJSON(),
      createdBy: this.$editData.roleInfo?.createdBy || AccountModule.userDetailInfo?.id || '',
      permissions: this.$editData.roleInfo!.permissions,
      user: this.$editData.roleInfo!.user,
    };
    if (this.$editData.isCreate) {
      this.addRole(newRole);
    } else {
      this.updateRole(newRole);
    }
  }
  public addRoleUsers(userIds: string[]) {
    const roleId = this.$editData.roleInfo!.id;
    const newUsers = companyAllActiveUser.filter((user) => userIds.includes(user.id));
    for (const role of this.$data) {
      if (role.id === roleId) {
        role.user = role.user.concat(newUsers);
        break;
      }
    }
  }
  public removeRoleUsers(userIds: string[]) {
    const roleId = this.$editData.roleInfo!.id;
    const newUsers = this.$editData.roleInfo!.user.filter((user) => !userIds.includes(user.id));
    for (const role of this.$data) {
      if (role.id === roleId) {
        role.user = newUsers;
        break;
      }
    }
  }
  public addRolePermissions(permissionIds: string[]) {
    const roleId = this.$editData.roleInfo!.id;
    const newPermissions = allPermission.filter((permission) => permissionIds.includes(permission.id));
    for (const role of this.$data) {
      if (role.id === roleId) {
        role.permissions = role.permissions.concat(newPermissions);
        break;
      }
    }
  }
  public delRolePermissions(permissionIds: string[]) {
    const roleId = this.$editData.roleInfo!.id;
    const newPermissions = this.$editData.roleInfo!.permissions.filter(
      (permission) => !permissionIds.includes(permission.id),
    );
    for (const role of this.$data) {
      if (role.id === roleId) {
        role.permissions = newPermissions;
        break;
      }
    }
  }
  public delRole(roleId: string) {
    const index = this.$data.findIndex((role) => role.id === roleId);
    this.$data.splice(index, 1);
  }
  public clearData() {
    this.$data.length = 0;
  }
}

export const roleManage = new RoleManage();

const hasEditPermission = (role: IRoleInfo, permissionNames: string[]) => {
  let editAll = false;
  if (permissionNames.includes('Role_Edit_All')) {
    editAll = true;
    return editAll;
  }
  let editOwn = false;
  if (permissionNames.includes('Role_Edit_Own')) {
    editOwn = role.createdBy === AccountModule.userDetailInfo?.id;
  }
  return editOwn;
};
const enrichRoleInfo = (roleList: IRoleInfo[]): ITableRoleInfo[] => {
  const result: ITableRoleInfo[] = [];
  for (const role of roleList) {
    result.push({
      ...role,
      permissions: sortPermissionsByGroupName(role.permissions),
      hasEditPermission: hasEditPermission(role, getPermissionNames()),
    });
  }
  return result;
};
